import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export interface Redirect {
  id: string;
  oldUrl: string;
  newUrl: string;
  status: 'active' | 'inactive';
  redirectType: '301' | '302';
  createdAt: string;
  updatedAt: string;
  hits?: number;
}

export interface CreateRedirectData {
  oldUrl: string;
  newUrl: string;
  status: 'active' | 'inactive';
  redirectType: '301' | '302';
}

export interface UpdateRedirectData {
  oldUrl?: string;
  newUrl?: string;
  status?: 'active' | 'inactive';
  redirectType?: '301' | '302';
}

class RedirectsAPI {
  private getAuthHeaders() {
    const token = localStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async getRedirects(): Promise<Redirect[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/redirects`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch redirects:', error);
      throw error;
    }
  }

  async getRedirect(id: string): Promise<Redirect> {
    try {
      const response = await axios.get(`${API_BASE_URL}/redirects/${id}`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch redirect:', error);
      throw error;
    }
  }

  async createRedirect(data: CreateRedirectData): Promise<Redirect> {
    try {
      const response = await axios.post(`${API_BASE_URL}/redirects`, data, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Failed to create redirect:', error);
      throw error;
    }
  }

  async updateRedirect(id: string, data: UpdateRedirectData): Promise<Redirect> {
    try {
      const response = await axios.put(`${API_BASE_URL}/redirects/${id}`, data, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Failed to update redirect:', error);
      throw error;
    }
  }

  async deleteRedirect(id: string): Promise<void> {
    try {
      await axios.delete(`${API_BASE_URL}/redirects/${id}`, {
        headers: this.getAuthHeaders(),
      });
    } catch (error) {
      console.error('Failed to delete redirect:', error);
      throw error;
    }
  }

  async toggleRedirectStatus(id: string): Promise<Redirect> {
    try {
      const response = await axios.patch(`${API_BASE_URL}/redirects/${id}/toggle`, {}, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Failed to toggle redirect status:', error);
      throw error;
    }
  }

  async getRedirectStats(): Promise<{ total: number; active: number; inactive: number }> {
    try {
      const response = await axios.get(`${API_BASE_URL}/redirects/stats`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch redirect stats:', error);
      throw error;
    }
  }
}

export const redirectsAPI = new RedirectsAPI(); 